import type { ProjectSummary } from '../lib/queries.js';
import { t } from '../lib/i18n.js';
import { MIN_PROJECT_DESCRIPTION_LENGTH } from '@aff/shared';

/**
 * Die Einrichtungsschritte eines Projekts.
 *
 * Ein Projekt ist erst dann wirklich fertig, wenn Beschreibung, Kanal,
 * Generator, Rezept und ein erster Lauf stehen. Vorher passiert
 * entweder nichts oder es kommt Mittelmaß heraus, und beides erklärt
 * dir sonst niemand. Deshalb steht das hier ganz oben, bis es erledigt ist.
 */

export interface SetupStep {
  key: string;
  title: string;
  why: string;
  done: boolean;
  optional?: boolean;
  /** Grobe Dauer in Minuten, für „noch ca. {n} Minuten" auf der Seite Einrichten */
  minutes?: number;
  action: { label: string; to: string };
}

/**
 * Wie lang die Beschreibung ist, an der die Einrichtung hängt.
 *
 * Je nach Projektart steht sie woanders: bei Apps im Briefing, bei Produkten
 * in der Produktbeschreibung, bei Unternehmen in Leistungen und Zielgruppe.
 * Die Seite Einrichten zeigt dieselbe Zahl im Profil an, deshalb steht die
 * Rechnung einmal hier.
 */
export function briefingLength(project: ProjectSummary): number {
  return project.type === 'product'
    ? (project.product?.description ?? '').trim().length
    : project.type === 'company'
      ? `${project.business?.services ?? ''} ${project.business?.audience ?? ''}`.trim().length
      : (project.briefing ?? '').trim().length;
}

export function buildSetupSteps(
  project: ProjectSummary,
  extras: { hasConnector: boolean; hasPosts: boolean; creditsOk: boolean; hasAgent: boolean; runActive?: boolean },
): SetupStep[] {
  const accounts = project.accounts ?? [];
  const connected = accounts.filter((a) => a.connected);
  const length = briefingLength(project);
  const subject = project.type === 'product' ? t('Produktbeschreibung')
    : project.type === 'company' ? t('Unternehmensbeschreibung') : t('Briefing');
  const base = `/projects/${project.id}`;
  const windows = project.schedule.windowLabels;

  /*
   * Reihenfolge wie im Handoff (§6.5): erst das, ohne das gar nichts geht
   * (Beschreibung, Kanal, Generator, Guthaben), dann das Rezept, dann der
   * erste Lauf, zuletzt die Automatik. Der frühere eigene Zeitplan-Schritt
   * steckt jetzt in der Automatik -- Uhrzeiten ohne eingeschaltete Automatik
   * tun nichts, zwei Schritte dafür haben das nur verschleiert.
   */
  return [
    {
      key: 'briefing',
      title: t('{subject} schreiben', { subject }),
      why: length >= MIN_PROJECT_DESCRIPTION_LENGTH
        ? t('Die Grundlage steht: {n} Zeichen über Angebot, Zielgruppe und Besonderheiten.', { n: length })
        : t('Beschreibe Angebot, Zielgruppe und Besonderheiten in mindestens {n} Zeichen. So kann die AI konkrete Beiträge schreiben.', { n: MIN_PROJECT_DESCRIPTION_LENGTH }),
      done: length >= MIN_PROJECT_DESCRIPTION_LENGTH,
      minutes: 3,
      // Das Profil wird auf der Seite Einrichten selbst bearbeitet.
      action: { label: t('Profil bearbeiten'), to: `${base}/setup?edit=profil` },
    },
    {
      /*
       * Das Briefing-Interview: optional, aber der größte Hebel für
       * Posts, die nach der Zielgruppe klingen statt nach Werbung.
       */
      key: 'interview',
      title: t('Briefing-Interview beantworten'),
      why: (project.briefingCheck?.score ?? 0) >= 75
        ? t('Die AI kennt dein Projekt zu {n} %. Hooks, Zahlen-Karten und Einwände kommen aus deinen Antworten.', { n: project.briefingCheck?.score ?? 0 })
        : t('Die AI kennt dein Projekt erst zu {n} %. Neun kurze Fragen, etwa fünf Minuten: Probleme in Kundenworten, belegte Zahlen, häufige Zweifel.', { n: project.briefingCheck?.score ?? 0 }),
      done: (project.briefingCheck?.score ?? 0) >= 75,
      optional: true,
      minutes: 5,
      action: { label: t('Fragen beantworten'), to: `${base}/setup?edit=profil#interview` },
    },
    {
      /*
       * Eigene Bilder sind kein Pflichtschritt, aber der größte Hebel für
       * glaubwürdige Beiträge -- und ohne eigenen Eintrag fand sie niemand:
       * Der Upload lag ganz unten im Projektprofil.
       */
      key: 'material',
      title: project.type === 'product' ? t('Produktfotos hochladen')
        : project.type === 'company' ? t('Eigene Fotos hochladen') : t('Screenshots der App hochladen'),
      why: (project.references?.length ?? 0) > 0
        ? t('{n} Bilder liegen als Vorlage vor. Ein Teil der Beiträge zeigt damit das Echte.', { n: project.references!.length })
        : project.type === 'app' || !project.type
          ? t('Aus drei bis sechs Screenshots wird die App-Folie: Ein Handy liegt beiläufig im Bild, deine App ist offen, pixelgenau.')
          : t('Mit drei bis sechs eigenen Fotos zeigen die Beiträge das Echte statt einer Erfindung des Bildmodells.'),
      done: (project.references?.length ?? 0) > 0,
      optional: true,
      minutes: 2,
      action: { label: t('Bilder hochladen'), to: `${base}/setup?edit=profil#material` },
    },
    {
      key: 'accounts',
      title: t('Kanäle verbinden (TikTok, Instagram, Facebook)'),
      why: connected.length
        ? t('{list} verbunden.', { list: connected.map((a) => a.handle || a.platform).join(` ${t('und')} `) })
        : t('Ohne verbundenen Kanal kann nichts veröffentlicht werden. Planen und Bauen geht trotzdem.'),
      done: connected.length > 0,
      minutes: 2,
      action: { label: t('Kanal verbinden'), to: `${base}/channels` },
    },
    {
      key: 'connectors',
      title: t('Bild-Generator hinterlegen'),
      /*
       * Der Generator gilt für ALLE Projekte, nicht für dieses eine. Der
       * Schritt bleibt trotzdem in der Projekt-Liste: Ohne Generator
       * entstehen hier graue Platzhalter, und das merkt man sonst erst nach
       * dem ersten Lauf.
       */
      why: extras.hasConnector
        ? t('Ein Generator ist eingerichtet. Es entstehen echte Bilder.')
        : t('Ohne API-Key entstehen nur graue Platzhalter. Ein Replicate-Key genügt und kostet unter einem Cent pro Bild. Gilt für alle deine Projekte.'),
      done: extras.hasConnector,
      minutes: 2,
      action: { label: t('Key hinterlegen'), to: `${base}/channels` },
    },
    {
      key: 'credits',
      title: t('Guthaben prüfen'),
      why: extras.creditsOk
        ? t('Guthaben ist vorhanden. Vor dem Start siehst du die konkrete Kostenschätzung.')
        : t('Für einen AI-Lauf brauchst du Guthaben. Die genauen Kosten zeigt die Vorschau vor dem Start.'),
      done: extras.creditsOk,
      minutes: 1,
      action: { label: t('Guthaben ansehen'), to: '/billing' },
    },
    {
      /*
       * Ohne Rezept baut weder ein Lauf von Hand noch die Automatik
       * irgendetwas.
       */
      key: 'agent',
      title: t('Rezept festlegen'),
      why: extras.hasAgent
        ? t('Es gibt ein Rezept, nach dem gebaut wird.')
        : t('Ein Rezept legt fest, wie ein Beitrag aussieht: Stil, Szenen, Ziel, Modelle. Ohne Rezept baut der Agent nichts.'),
      done: extras.hasAgent,
      minutes: 2,
      action: { label: t('Rezept anlegen'), to: `${base}/recipes` },
    },
    {
      key: 'firstrun',
      title: t('Ersten Lauf starten'),
      why: extras.hasPosts
        ? t('Es liegen bereits Beiträge vor.')
        : extras.runActive
          ? t('Läuft gerade. In ein, zwei Minuten liegen die Entwürfe unter Prüfen.')
          : t('Der Agent baut die ersten Beiträge. Vorher siehst du die Kosten, und nichts geht live, bevor du es freigibst.'),
      // Ein laufender erster Lauf ist erledigt, was die Einrichtung angeht: nichts mehr zu klicken.
      done: extras.hasPosts || !!extras.runActive,
      minutes: 1,
      // „Beiträge bauen" ist eine eigene Seite.
      action: extras.runActive && !extras.hasPosts
        ? { label: t('Zusehen'), to: `${base}/run` }
        : { label: t('Jetzt Beiträge bauen'), to: `${base}/build` },
    },
    {
      key: 'autopilot',
      title: t('Automatik einschalten'),
      why: !project.autopilot.enabled && project.autopilot.armed
        ? t('Eingestellt. Startet von selbst, sobald TikTok, Instagram oder Facebook verbunden ist.')
        : !project.autopilot.enabled
        ? t('Optional. Danach baut der Agent von selbst nach deinem Wochenplan.')
        : !windows.length
          ? t('Kein Zeitfenster aktiv. Dann kann nichts eingeplant werden.')
          : project.autopilot.mode === 'full'
            ? `${t('Vollautomatisch, um {windows} Uhr.', { windows: windows.join(', ') })} ${t('Instagram und Facebook veröffentlichen automatisch zum Termin. Bei TikTok landet der Post als Entwurf in deiner TikTok-App, solange die App-Prüfung läuft.')}`
            : t('Mit Freigabe: Der Agent baut von selbst nach, du gibst frei. Veröffentlicht um {windows} Uhr.', { windows: windows.join(', ') }),
      done: (project.autopilot.enabled || !!project.autopilot.armed) && windows.length > 0,
      optional: true,
      minutes: 1,
      action: { label: t('Automatik einstellen'), to: `${base}/automation` },
    },
  ];
}
